import type { Geometry } from "geojson";
import { getApiBaseUrl } from "src/config/Endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

const API_BASE_URL = getApiBaseUrl();

const buildSearchParams = (
  params: Record<string, string | number | undefined>
) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "" && value !== null) {
      searchParams.set(key, String(value));
    }
  });
  return searchParams.toString();
};

const inFlightRequests = new Map<string, Promise<unknown>>();

const getStoredTenantId = (): number | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const rawTenantId = window.localStorage.getItem("tenant_id");
  if (!rawTenantId) {
    return undefined;
  }

  const parsedTenantId = Number(rawTenantId);
  return Number.isFinite(parsedTenantId) && parsedTenantId > 0
    ? parsedTenantId
    : undefined;
};

const fetchWithDedupe = <T,>(key: string, factory: () => Promise<T>): Promise<T> => {
  const existing = inFlightRequests.get(key) as Promise<T> | undefined;
  if (existing) {
    return existing;
  }

  const requestPromise = factory().finally(() => {
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, requestPromise);
  return requestPromise;
};

// --- Types ---

export interface TrendPoint {
  date: string;
  dateFormattedValue?: string;
  value: number;
  valueFormattedValue?: string;
}

export interface EngagementTrendPoint {
  value: number;
  period: string;
  moMPercent: number;
}

export interface FollowersTrendPayload {
  daily?: TrendPoint[];
  monthly?: TrendPoint[];
}

export interface ContentTrendKpiPoint {
  date: string;
  impressions?: number;
  reach?: number;
  viewers?: number;
  engagement_rate?: number;
  engagementRate?: number;
}

export type ContentTrendKpiPayload =
  | ContentTrendKpiPoint[]
  | {
      daily?: ContentTrendKpiPoint[];
      monthly?: ContentTrendKpiPoint[];
    };

export interface OrganicKpiCard {
  title: string;
  value: string;
  comparison_label?: string;
  comparisonLabel?: string;
  tooltip_label?: string;
  tooltipLabel?: string;
  trend: TrendPoint[];
  trendData?: EngagementTrendPoint[];
  change_percent?: number | null;
  percentageChange?: number | null;

  percentageChangeFormattedValue?: string | null;

  id?: string | null;
  metric_key?: string | null;
  metricKey?: string | null;
  metric?: string | null;
}

type EngagementMetricValue =
  | number
  | {
      value?: number;
      count?: number;
      total?: number;
      change_percent?: number | null;
      percentageChange?: number | null;
      comparison_label?: string;
      comparisonLabel?: string;
      trendData?: EngagementTrendPoint[];
      trend?: TrendPoint[];
      valueFormattedValue?: string;
      percentageChangeFormattedValue?: string | null;
    };

export interface FacebookContentReportResponse {
  kpis: OrganicKpiCard[];
  trend_kpi?: ContentTrendKpiPayload;
  trends_kpi?: ContentTrendKpiPayload;
  trend_granularity?: "daily" | "monthly";
  total_engagements: {
    viewers?: EngagementMetricValue;
    impressions?: EngagementMetricValue;
    reach?: EngagementMetricValue;
    link_clicks?: EngagementMetricValue;
    clicks?: EngagementMetricValue;
    shares?: EngagementMetricValue;
    comments?: EngagementMetricValue;
    love?: EngagementMetricValue;
    angry?: EngagementMetricValue;
    wow?: EngagementMetricValue;
    haha?: EngagementMetricValue;
    reaction?: {
      like?: EngagementMetricValue;
      love?: EngagementMetricValue;
      shares?: EngagementMetricValue;
      haha?: EngagementMetricValue;
      angry?: EngagementMetricValue;
      wow?: EngagementMetricValue;
      sorry?: EngagementMetricValue;
      likeFormattedValue?: string;
      loveFormattedValue?: string;
      sharesFormattedValue?: string;
      hahaFormattedValue?: string;
      angryFormattedValue?: string;
      wowFormattedValue?: string;
      sorryFormattedValue?: string;
      // Aggregate reactions trend/change (total across all reaction types), siblings
      // of the individual per-type counts above rather than nested under them.
      change_percent?: number | null;
      percentageChange?: number | null;
      percentageChangeFormattedValue?: string | null;
      comparison_label?: string;
      comparisonLabel?: string;
      trendData?: EngagementTrendPoint[];
    };
  };
  posts_by_type: Array<{ name: string; value: number; valueFormattedValue?: string; color?: string }>;
  post_type_over_time: Array<Record<string, string | number>>;
  post_type_metrics: Array<{
    type: string;
    engagementRate: number;
    engagementRateFormattedValue?: string;
    organicReach: number;
    ctr: number;
    ctrFormattedValue?: string;
    viewers?: number;
    viewersFormattedValue?: string;
    impressions: number;
    impressionsFormattedValue?: string;
    link_clicks?: number;
    link_clicksFormattedValue?: string;
    clicks?: number;
  }>;
  post_details: Array<{
    post_system_id?: string;
    post_id?: string;
    id?: string;
    caption?: string;
    message?: string;
    post_type?: string;
    type?: string;
    engagement_rate?: number;
    engagement_rateFormattedValue?: string;
    engagementRate?: number;
    ctr?: number;
    ctrFormattedValue?: string;
    viewers?: number;
    impressions?: number;
    impressionsFormattedValue?: string;
    reach?: number;
    organic_reach?: number;
    link_clicks?: number;
    link_clicksFormattedValue?: string;
    clicks?: number;
    comments?: number;
    commentsFormattedValue?: string;
    loves?: number;
    likes?: number;
    likesFormattedValue?: string;
    shares?: number;
    sharesFormattedValue?: string;
    post_url?: string;
    permalink_url?: string;
    permalink?: string;
    url?: string;
    link?: string;
    thumbnail_url?: string;
    thumbnail?: string;
    full_picture?: string;
    picture?: string;
    media_url?: string;
    image_url?: string;
    created_time?: string;
    created_at?: string;
    date?: string;
  }>;
}

export interface FollowersReportResponse {
  kpis: OrganicKpiCard[];
  follower_trend?: FollowersTrendPayload;
  countries: Array<{
    name: string;
    followers: number;
    followersFormattedValue?: string;
    change_percent?: number;
  }>;
  cities: Array<{
    name: string;
    followers: number;
    followersFormattedValue?: string;
    change_percent?: number;
    /** When both are set, the city map uses them and skips client geocoding (faster, stable). */
    latitude?: number;
    longitude?: number;
    /**
     * Optional GeoJSON Polygon/MultiPolygon for a choropleth-style fill (same idea as country shapes).
     * Not used by the UI until the map layer reads it — requires backend to attach simplified geometry.
     */
    boundary?: Geometry;
  }>;
}

export interface InstagramFollowersReportResponse
  extends FollowersReportResponse {
  gender_demographics: Array<{
    key: "male" | "female" | "unknown";
    percent: number;
    comparison_label?: string;
    ages: Array<{ range: string; count: number }>;
  }>;
}

export interface InstagramContentReportResponse {
  kpis: OrganicKpiCard[];
  total_engagements: {
    impressions?: EngagementMetricValue;
    likes?: EngagementMetricValue;
    reactions?: {
      like?: EngagementMetricValue;
      likeFormattedValue?: string;
      // Aggregate reactions trend/change (total across all reaction types), siblings
      // of the individual `like` count above rather than nested under it.
      change_percent?: number | null;
      percentageChange?: number | null;
      percentageChangeFormattedValue?: string | null;
      comparison_label?: string;
      comparisonLabel?: string;
      trendData?: EngagementTrendPoint[];
    };
    shares?: EngagementMetricValue;
    comments?: EngagementMetricValue;
    saves?: EngagementMetricValue;
    profile_visits?: EngagementMetricValue;
    new_followers?: EngagementMetricValue;
  };
  posts_by_type: Array<{ name: string; value: number; valueFormattedValue?: string; color?: string }>;
  post_type_over_time: Array<Record<string, string | number>>;
  post_type_metrics: Array<{
    type: string;
    engagementRate: number;
    engagementRateFormattedValue?: string;
    reach: number;
    reachFormattedValue?: string;
    impressions: number;
    impressionsFormattedValue?: string;
  }>;
  impressions_vs_reach_trend?: Array<{
    date: string;
    impressions: number;
    reach: number;
  }>;
  trend_kpi?: ContentTrendKpiPayload;
  trends_kpi?: ContentTrendKpiPayload;
  trend_granularity?: "daily" | "monthly";
  post_details: Array<{
    post_system_id?: string;
    post_id?: string;
    id?: string;
    caption?: string;
    message?: string;
    media_type?: string;
    post_type?: string;
    type?: string;
    engagement_rate?: number;
    engagement_rateFormattedValue?: string;
    engagementRate?: number;
    impressions?: number;
    impressionsFormattedValue?: string;
    reach?: number;
    reachFormattedValue?: string;
    likes?: number;
    likesFormattedValue?: string;
    reactions?: {
      like?: number;
      likeFormattedValue?: string;
    };
    comments?: number;
    commentsFormattedValue?: string;
    shares?: number;
    sharesFormattedValue?: string;
    saves?: number;
    savesFormattedValue?: string;
    permalink_url?: string;
    post_url?: string;
    permalink?: string;
    url?: string;
    link?: string;
    media_url?: string;
    thumbnail_url?: string;
    thumbnail?: string;
    image_url?: string;
    created_time?: string;
    created_at?: string;
    date?: string;
  }>;
}

// --- Service ---

export const organicMediaService = {
  fetchPostCaptions: async (params: {
    start_date: string;
    end_date: string;
    tenant_id?: number;
    lang: ApiLang;
  }): Promise<{ post_captions: string[] }> => {
    const tenantId = params.tenant_id ?? getStoredTenantId();
    const query = buildSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
      tenant_id: tenantId,
      lang: normalizeApiLang(params.lang),
    });
    const url = `${API_BASE_URL}/organic-media/post-captions?${query}`;
    return fetchWithDedupe(url, async () => {
      const response = await fetch(url, { method: "GET", headers: buildAuthHeaders("api_key") });
      return handleJsonResponse(response);
    });
  },

  fetchInstagramPostCaptions: async (params: {
    start_date: string;
    end_date: string;
    tenant_id?: number;
    lang: ApiLang;
  }): Promise<{ post_captions: string[] }> => {
    const tenantId = params.tenant_id ?? getStoredTenantId();
    const query = buildSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
      tenant_id: tenantId,
      lang: normalizeApiLang(params.lang),
    });
    const url = `${API_BASE_URL}/organic-media/instagram-post-captions?${query}`;
    return fetchWithDedupe(url, async () => {
      const response = await fetch(url, { method: "GET", headers: buildAuthHeaders("api_key") });
      return handleJsonResponse(response);
    });
  },

  fetchFacebookContentReport: async (params: {
    start_date: string;
    end_date: string;
    post_caption?: string;
    tenant_id?: number;
    lang: ApiLang;
    trend_granularity?: "daily" | "monthly";
  }): Promise<FacebookContentReportResponse> => {
    const tenantId = params.tenant_id ?? getStoredTenantId();
    const searchParams = new URLSearchParams();
    searchParams.set("start_date", params.start_date);
    searchParams.set("end_date", params.end_date);
    searchParams.set("lang", normalizeApiLang(params.lang));
    if (tenantId !== undefined && tenantId !== null) {
      searchParams.set("tenant_id", String(tenantId));
    }
    if (params.post_caption) {
      searchParams.set("post_caption", params.post_caption);
    }
    if (params.trend_granularity) {
      searchParams.set("trend_granularity", params.trend_granularity);
    }
    const query = searchParams.toString();
    const url = `${API_BASE_URL}/organic-media/facebook-content-report?${query}`;
    return fetchWithDedupe(url, async () => {
      const response = await fetch(url, { method: "GET", headers: buildAuthHeaders("api_key") });
      return handleJsonResponse(response);
    });
  },

  fetchInstagramContentReport: async (params: {
    start_date: string;
    end_date: string;
    post_caption?: string;
    tenant_id?: number;
    lang: ApiLang;
    trend_granularity?: "daily" | "monthly";
  }): Promise<InstagramContentReportResponse> => {
    const tenantId = params.tenant_id ?? getStoredTenantId();
    const searchParams = new URLSearchParams();
    searchParams.set("start_date", params.start_date);
    searchParams.set("end_date", params.end_date);
    searchParams.set("lang", normalizeApiLang(params.lang));
    if (tenantId !== undefined && tenantId !== null) {
      searchParams.set("tenant_id", String(tenantId));
    }
    if (params.post_caption) {
      searchParams.set("post_caption", params.post_caption);
    }
    if (params.trend_granularity) {
      searchParams.set("trend_granularity", params.trend_granularity);
    }
    const query = searchParams.toString();
    const url = `${API_BASE_URL}/organic-media/instagram-content-report?${query}`;
    return fetchWithDedupe(url, async () => {
      const response = await fetch(url, { method: "GET", headers: buildAuthHeaders("api_key") });
      return handleJsonResponse(response);
    });
  },

  fetchFacebookFollowersReport: async (params: {
    start_date: string;
    end_date: string;
    top_countries?: number;
    top_cities?: number;
    tenant_id?: number;
    lang: ApiLang;
  }): Promise<FollowersReportResponse> => {
    const tenantId = params.tenant_id ?? getStoredTenantId();
    const query = buildSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
      top_countries: params.top_countries,
      top_cities: params.top_cities,
      tenant_id: tenantId,
      lang: normalizeApiLang(params.lang),
    });
    const url = `${API_BASE_URL}/organic-media/facebook-followers-report?${query}`;
    return fetchWithDedupe(url, async () => {
      const response = await fetch(url, { method: "GET", headers: buildAuthHeaders("api_key") });
      return handleJsonResponse(response);
    });
  },

  fetchInstagramFollowersReport: async (params: {
    start_date: string;
    end_date: string;
    top_countries?: number;
    top_cities?: number;
    tenant_id?: number;
    lang: ApiLang;
  }): Promise<InstagramFollowersReportResponse> => {
    const tenantId = params.tenant_id ?? getStoredTenantId();
    const query = buildSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
      top_countries: params.top_countries,
      top_cities: params.top_cities,
      tenant_id: tenantId,
      lang: normalizeApiLang(params.lang),
    });
    const url = `${API_BASE_URL}/organic-media/instagram-followers-report?${query}`;
    return fetchWithDedupe(url, async () => {
      const response = await fetch(url, { method: "GET", headers: buildAuthHeaders("api_key") });
      return handleJsonResponse(response);
    });
  },
};
